import { useEffect } from 'react';
import { db, auth } from '../firebase';
import { getMessaging, onMessage, getToken } from 'firebase/messaging';
import { doc, getDoc, updateDoc, serverTimestamp, deleteField } from 'firebase/firestore';
import { requestMessagingToken } from '../firebase';

const NotificationHandler = () => {
  useEffect(() => {
    const user = auth.currentUser;
    if (!user) return;

    const initializeNotifications = async () => {
      try {
        // Check user's notification preferences first
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        const userData = userDoc.data();
        const preferences = userData?.notificationPreferences;
        const fcmEnabled = preferences?.fcm?.enabled ?? true; // Default to enabled if not set

        if (!fcmEnabled) {
          console.log('Web FCM notifications disabled in preferences, skipping token registration');
          return;
        }

        // First, request notification permission if not already granted
        if (Notification.permission !== 'granted') {
          const permission = await Notification.requestPermission();
          if (permission !== 'granted') {
            console.log('Notification permission denied');
            return;
          }
        }

        // Get messaging instance and token
        const messaging = getMessaging();
        const token = await requestMessagingToken();
        console.log('FCM Token:', token);
        
        if (token && auth.currentUser) {
          // Store token in user document with web token type (don't overwrite mobile token)
          const userRef = doc(db, 'users', auth.currentUser.uid);
          await updateDoc(userRef, {
            fcmTokenWeb: token, // Store web token separately
            fcmToken: token, // Keep for backward compatibility
            fcmTokenType: 'web', // Mark as web token
            fcmTokenUpdated: serverTimestamp()
          });
          console.log('FCM token stored for user (web)');
        }

        // Handle foreground messages
        onMessage(messaging, (payload) => {
          console.log("Received foreground message:", payload);
          
          // Force show notification even in foreground
          if (payload.notification) {
            // Get current permission state
            if (Notification.permission === "granted") {
              // Create notification
              const notification = new Notification(payload.notification.title, {
                body: payload.notification.body,
                icon: "/TOM-logo-512.png",
                badge: "/TOM-logo-512.png",
                data: {
                  url: payload.fcmOptions?.link
                }
              });

              // Handle click
              notification.onclick = () => {
                if (payload.fcmOptions?.link) {
                  window.open(payload.fcmOptions.link, '_blank');
                }
              };
            }
          }
        });

      } catch (error) {
        console.error('Error setting up notifications:', error);
      }
    };

    initializeNotifications();
  }, []);

  useEffect(() => {
    const user = auth.currentUser;
    if (!user) return;

    const validateFCMToken = async () => {
      // Get user doc to check FCM token and preferences
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      const userData = userDoc.data();
      const preferences = userData?.notificationPreferences;
      const fcmEnabled = preferences?.fcm?.enabled ?? true; // Default to enabled if not set

      if (!fcmEnabled) {
        console.log('Web FCM notifications disabled, skipping token validation');
        // If disabled, delete web token
        if (userData?.fcmTokenWeb || (userData?.fcmTokenType === 'web' && userData?.fcmToken)) {
          await updateDoc(doc(db, 'users', user.uid), {
            fcmTokenWeb: deleteField(),
            ...(userData?.fcmTokenType === 'web' ? { fcmToken: deleteField(), fcmTokenType: deleteField() } : {})
          });
          console.log('Web FCM token deleted (preference disabled)');
        }
        return;
      }

      if (!userData?.fcmToken && !userData?.fcmTokenWeb) {
        console.warn('No FCM token found for user');
        return;
      }
      
      try {
        const messaging = getMessaging();
        const currentToken = await getToken(messaging);
        
        const currentWebToken = userData.fcmTokenWeb || (userData.fcmTokenType === 'web' ? userData.fcmToken : null);
        if (currentToken !== currentWebToken) {
          console.log('FCM token mismatch, updating...');
          await updateDoc(doc(db, 'users', user.uid), {
            fcmTokenWeb: currentToken, // Store web token separately
            fcmToken: currentToken, // Keep for backward compatibility
            fcmTokenType: 'web', // Mark as web token
            fcmTokenUpdated: serverTimestamp()
          });
        }
      } catch (error) {
        console.error('Error validating FCM token:', error);
      }
    };

    validateFCMToken();
  }, []);  // Only run once on mount since we get fresh user data each time

  return null;
};

export default NotificationHandler;
